import React, { FC, useEffect, useRef, useState } from "react";
import { Input, Button, Modal, Row, Col } from "antd";
import {
  MinusOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import _ from "lodash";
import { ReactSVG } from "react-svg";
import { wrapRequestPayload } from "../../../helpers/request/RequestPayload";
import {
  getPageRequestPublishTopic,
  getPageSubscriptionTopic,
  getPageEotSubscriptionTopic,
  getUsersSubscriptionTopic,
  getSearchMessageSubscriptionTopic,
  getSearchMessagePublishTopic,
  getFocusMessagePublishTopic,
  getPageGroupMembersSubscriptionTopic,
  getAggregateBdsPublishTopic,
  getAggregateBdsSubscriptionTopic,
  getBdsEotSubscriptionTopic,
} from "hooks/MQTTConstants";
import { useLocation } from "react-router-dom";
import { SocketTopics } from "constants/enums";
import { useSelector } from "react-redux";
import { RootState } from "reducers/rootReducer";
import moment from "moment";
import { useDispatch } from "react-redux";
import { CLEAR_SUMMARY } from "reducers/reducer";
import { ZoomInOutlined, ZoomOutOutlined } from "@ant-design/icons";
import { generateUUID } from "utils/appUtil";
import PersonItem from "../../../common/PersonItem";
import LocationItem from "../../../common/LocationItem";
import { formatForRedis, attributeKeyGenerator } from "utils/appUtil";
import html2PDF from 'jspdf-html2canvas';
import { GET_AGGREGATE } from "../../../reducers/reducer";
import useSocketActions from "../../../hooks/socketActions";

const { TextArea } = Input;
let timelineInterval;
const DEFAULT_LIMIT = 720;
const DEFAULT_ZOOM = 20;
const TIMELINE_SPEEDS = [1, 2, 5, 7, 10, 15, 20, 30, 50, 100];

const AnalyticsSummaryModal: FC<{ socket: ISocket; params?: any }> = ({
  socket,
  params,
}) => {
  const { search } = useLocation();
  const { page, users, messages, requestIds } = useSelector(
    (state: RootState) => state.auth
  );
  const [pageInfo, setPageInfo] = useState<any[]>([]);
  const [pageLocation, setPageLocation] = useState<any>();
  const dispatch = useDispatch();
  const [messageBreakTime, setMessageBreakTime] = useState<any[]>([]);
  const [timeDiff, setTimeDiff] = useState<any>({
    offset: 0,
    limit: DEFAULT_LIMIT,
    length: 0,
  });
  const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);
  const [play, setPlay] = useState<boolean>(false);
  const [playSpeed, setPlaySpeed] = useState<number>(2);
  const timelineRef = useRef(null);
  const {handleMessages} = useSocketActions();

  const getTime = (time: number, date = false): string => {
    const startTime = messages?.[0]?.createdAt;
    const momentTime = moment(startTime + time * 60 * 1000).format(
      `${date ? "MMM D " : ""}hh:mm A`
    );
    return momentTime;
  };

  const checkin = (time: number) => {
    let points = messageBreakTime.filter((bt) => bt.time === time);
    if (points.length > 0) return points;
    return null;
  };

  const getMessages = (pageId) => {
    const requestId = generateUUID();
    localStorage.setItem("requestId", requestId);
    localStorage.setItem("requestService", "summary");
    const messageRequest: any = {
      select: {
        index: {
          searchIndex: `${process.env.REACT_APP_ECO_SYSTEM}:message:createdAt`,
          searchQuery: `@pageId:{${pageId.toString()}}`,
          searchOptions: 'TIMEOUT 100',
        },
        load: {object: '$'},
      },
      operations: [
        {
          sortBy: {
            properties: [
              {
                field: '@createdAt',
                order: 'DESC',
              },
            ],
          },
        },
      ],
      result: {
        pagination: {
          offset: 0,
          limit: 100,
        },
      },
      sortBy: {
        properties: [
          {
            field: "@createdAt",
            order: "DESC",
          },
        ],
        max: 10000,
      },
    };
    dispatch(
      GET_AGGREGATE({
        requestData: messageRequest,
        callback: (response) => {
          console.log('message response', response);
          handleMessages(requestId, response)
        }
      }));
  };

  useEffect(() => {
    dispatch({ type: CLEAR_SUMMARY });
    if (socket.client) {
      const query = new URLSearchParams(search);
      const connectionId = query.get("connectionId");
      const pageId = query.get("pageId");
      const cudbServiceId = query.get("cudbServiceId");
      socket.subscribeToEvent(
        SocketTopics.SubscribePageTopics,
        getPageSubscriptionTopic(connectionId)
      );
      socket.subscribeToEvent(
        SocketTopics.SubscribePageTopics,
        getPageEotSubscriptionTopic(pageId)
      );
      socket.subscribeToEvent(
        SocketTopics.SubscribePageTopics,
        getUsersSubscriptionTopic(connectionId, cudbServiceId)
      );
      socket.subscribeToEvent(
        SocketTopics.SubscribePageTopics,
        getSearchMessageSubscriptionTopic(pageId, connectionId)
      );
      if (pageId) {
        socket.publishToEvent(
          SocketTopics.PublishPageTopics,
          getPageRequestPublishTopic(connectionId),
          wrapRequestPayload(
            {
              objectId: pageId,
              paths: ["$"],
              returnPaths: ["$"],
            },
            pageId
          )
        );
        socket.subscribeToEvent(
          SocketTopics.SubscribePageTopics,
          getPageGroupMembersSubscriptionTopic(pageId)
        );
        getMessages(pageId);
      }
    }
  }, [search, socket.client]);

  const getPageUsers = (ids) => {
    const query = new URLSearchParams(search);
    const cudbServiceId = query.get("cudbServiceId");
    const connectionId = query.get("connectionId");
    const userRequest: any = {
      select: {
        index: {
          searchIndex: `${process.env.REACT_APP_ECO_SYSTEM}:userNameIndex`,
          searchQuery: `@serviceId:{'${formatForRedis(cudbServiceId)}'}`,
          searchOptions: "TIMEOUT 5000",
        },
        list: {
          objectIds: ids,
        },
        load: {
          id: "$.objectId",
          status: "$.nonpersistent.static.live.status",
          live: "$.persistent.static.live",
        },
      },
      apply: {
        expression: "",
        name: "",
      },
      result: {},
      filter: {
        expression: ``,
      },
      anyStatus: false,
    };
    socket.publishToEvent(
      SocketTopics.PublishPageTopics,
      getAggregateBdsPublishTopic(connectionId),
      wrapRequestPayload(userRequest, requestIds.user)
    );
  };

  useEffect(() => {
    if (page) {
      const query = new URLSearchParams(search);
      const connectionId = query.get("connectionId");
      const pageId = query.get("pageId");
      if (pageId !== page.objectId) return;
      let info = [];
      const onMove = page?.persistent?.static?.profile;
      if (onMove.type === "cad") {
        Object.keys(onMove).map((key) => {
          if (typeof onMove[key] === "string") {
            info.push({ name: attributeKeyGenerator(key), value: onMove[key] });
          }
          else if(onMove[key]?.type === 'location' || onMove[key]?.type === 'incident-location'){
            setPageLocation({ name: attributeKeyGenerator(key), value: onMove[key] });
          }
        });
      } else {
        info = [
          { name: "Incident Type", value: "Hail ride" },
          { name: "First Name", value: "N/A" },
          { name: "Last Name", value: "N/A" },
          { name: "Phone", value: "N/A" },
          { name: "Alternative Phone", value: "N/A" },
        ];
      }
      info = [
        ...info,
        {
          name: "Dispatch Time",
          value: getLocalDatetimeString(
            page?.persistent?.static?.metadata?.createdAt
          ),
        },
        {
          name: "Start Time",
          value: getLocalDatetimeString(
            page?.persistent?.static?.metadata?.createdAt
          ),
        },
        { name: "Stop Time", value: "N/A" },
      ];
      setPageInfo(info);
      const members = page?.persistent?.static?.metadata?.members?.users ?? {};
      socket.subscribeToEvent(
        SocketTopics.SubscribePageTopics,
        getAggregateBdsSubscriptionTopic(connectionId)
      );
      getPageUsers(Object.keys(members));
    }
  }, [page]);

  useEffect(() => {
    if (users && socket.client) {
      const query = new URLSearchParams(search);
      const cudbServiceId = query.get("cudbServiceId");

      users.map((user) => {
        socket.subscribeToEvent(
          SocketTopics.SubscribePageTopics,
          getBdsEotSubscriptionTopic(cudbServiceId, user.id)
        );
      });
    }
  }, [users, socket.client]);

  const getEventIcon = (event) => {
    switch (event.messageType) {
      case "place":
        return { icon: "Place-icon", color: "purple" };
      case "video-call":
        return { icon: "Audio-icon", color: "green" };
      case "b2t-call":
        return { icon: "B2T-icon", color: "green" };
      case "beam-assist":
        return { icon: "Incident-icon", color: "orange" };
      case "capture":
        return { icon: "Capture-icon", color: "gray" };
      case "alert":
        return { icon: "Alert-icon", color: "yellow" };
      case "gps":
        return { icon: "iot_blue", color: "purple" };
      case "gps-stop":
        return { icon: "iot_blue", color: "purple" };
      case "gps-report":
        return { icon: "IoT File-icon", color: "green" };
      case "media":
        return { icon: "Image-icon", color: "blue" };
      case "rsvp":
        return { icon: "RSVP-icon", color: "yellow" };
      case "rsvp-response":
        return { icon: "RSVP-icon", color: "yellow" };
      case "video-recording":
        return { icon: "Video-icon", color: "blue" };
      case "sound-recording":
        return { icon: "Audio-icon", color: "green" };
      case "event":
        return { icon: "Event-icon", color: "orange" };
    }
  };

  const getLocalDatetimeString = (date) => {
    const dateObject = new Date(date);
    return dateObject
      ? `${dateObject.toLocaleDateString()} ${dateObject.toLocaleTimeString()}`
      : "N/A";
  };

  useEffect(() => {
    if (messages && messages.length > 0) {
      const startTime = messages?.[0]?.createdAt;
      const endTime = messages?.[messages.length - 1]?.createdAt;
      const messageBreakTime = [];
      messages.map((message) => {
        const eventIcon = getEventIcon(message);
        messageBreakTime.push({
          time: Math.floor((message.createdAt - startTime) / 1000 / 60),
          name: eventIcon?.icon,
          icon: eventIcon?.color,
          type: message.messageType.toUpperCase(),
          id: message.id,
        });
      });
      const diffMinutes = (endTime - startTime) / 1000 / 60;
      setMessageBreakTime(messageBreakTime);
      if (timeDiff.offset === 0) {
        setTimeDiff({ offset: 0, limit: 720, length: diffMinutes });
      } else {
        setTimeDiff({
          offset: timeDiff.offset,
          limit: timeDiff.limit,
          length: diffMinutes,
        });
      }
    }
  }, [messages]);

  useEffect(() => {
    if (play) {
      if (timelineInterval) clearInterval(timelineInterval);
      timelineInterval = setInterval(
        (speed) => {
          if (
            timelineRef.current.scrollLeft <
            timelineRef.current.scrollWidth - timelineRef.current.clientWidth
          ) {
            timelineRef.current.scrollLeft += TIMELINE_SPEEDS[speed];
          }
        },
        10,
        playSpeed
      );
    } else if (timelineInterval) {
      clearInterval(timelineInterval);
    }
  }, [play, playSpeed]);

  return (
    <Modal
      title=""
      visible={true}
      width="100%"
      className="beam-modal analytics-summary-modal"
      footer={[]}
      destroyOnClose={true}
    >
      {page && (
        <React.Fragment>
          <Row>
            <Col span={12} className="pr-3">
              <Row className="d-flex">
                <Col>
                  <h3 className="mb-1">
                    {page?.persistent?.static?.metadata?.title}
                  </h3>
                  <h4>Incidents information</h4>
                </Col>
                <Col>
                  <Button className="beam-btn icon-btn ml-2">
                    <ReactSVG
                      src="/images/flag-icon.svg"
                      className={`${
                        page?.nonpersistent?.static?.live?.status?.name ??
                        "gray"
                      }-icon`}
                    />
                  </Button>
                  <Button className="beam-btn icon-btn ml-2">
                    <ReactSVG src="/images/iot-icon.svg" />
                  </Button>
                  <Button
                    className="beam-btn icon-btn ml-2"
                    onClick={()=>{
                      html2PDF(document.getElementsByClassName('ant-modal-content')[0], {
                        jsPDF: {
                          format: 'a4',
                        },
                        html2canvas: {
                          scrollX: 0,
                          scrollY: -window.scrollY,
                        },
                        imageType: 'image/jpeg',
                        output: './pdf/generate.pdf'
                      });
                    }}
                  >
                    <ReactSVG src="/images/export-pdf.svg" className="export-svg" />
                  </Button>
                </Col>
              </Row>
              <Row>
                <Col span={24}>
                  <ul className="incident-information mt-2">
                    {pageInfo.map((item, i) => (
                      <li key={i}>
                        <Row>
                          <Col span={12}>
                            <p>{item.name}</p>
                          </Col>
                          <Col span={12}>
                            <p>{item.value}</p>
                          </Col>
                        </Row>
                      </li>
                    ))}
                  </ul>
                </Col>
              </Row>
            </Col>
            <Col span={12} className="pl-3 mt-3">
              <h4 className="mt-3">Locations</h4>
              <Row className="locations mt-2">
                <Col span={24}>
                  {page?.persistent?.static?.profile.type === "cad" && pageLocation && (
                    <React.Fragment>
                      <LocationItem
                        type="green"
                        title={pageLocation.name}
                        locationObj={
                          pageLocation.value
                        }
                      />
                      {/* <LocationItem
                        type="blue"
                        title="Caller location"
                        locationObj={
                          page?.persistent?.static?.profile.callerLocation
                        }
                      /> */}
                    </React.Fragment>
                  )}
                  {page?.persistent?.static?.profile.type !== "cad" && (
                    <React.Fragment>
                      <LocationItem
                        type="green"
                        title="Incident location"
                        locationObj={page?.persistent?.static?.live.location}
                      />
                    </React.Fragment>
                  )}
                </Col>
              </Row>
              <Row className="mt-3">
                <Col span={24}>
                  <TextArea
                    autoSize={{ minRows: 3, maxRows: 3 }}
                    placeholder="Protocals"
                  />
                </Col>
              </Row>
              <Row className="d-flex px-3">
                <Col>
                  <p>Optional</p>
                </Col>
                <Col>
                  <p>max. 500 character</p>
                </Col>
              </Row>
              <Row className="d-flex mt-3">
                <Col span={8} className="pr-2">
                  <Button className="beam-btn w-full">Survey</Button>
                </Col>
                <Col span={8} className="px-2">
                  <Button className="beam-btn w-full">NCIC</Button>
                </Col>
                <Col span={8} className="pl-2">
                  <Button className="beam-btn w-full">NFIRS</Button>
                </Col>
              </Row>
              <Row className="d-flex mt-3">
                <Col span={24}>
                  <Button className="beam-btn w-full green-btn cover">
                    End Event
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <h4 className="mt-3">People On Page</h4>
            </Col>
          </Row>
          <Row className="mt-3">
            {users.map((item, i) => (
              <Col span={12} className={i % 2 === 0 ? "pr-3" : ""}>
                <PersonItem
                  onClick={() => {}}
                  avatar="/images/users/user.png"
                  userObj={item}
                />
              </Col>
            ))}
          </Row>

          <div className="mt-4 timeline-zoom-options">
            {!play && (
              <Button
                className="beam-btn icon-btn mr-2"
                onClick={() => {
                  setPlay(true);
                }}
              >
                <PlayCircleOutlined />
              </Button>
            )}
            {play && (
              <React.Fragment>
                <Button
                  className="beam-btn icon-btn mr-2"
                  onClick={() => {
                    setPlay(false);
                  }}
                >
                  <PauseCircleOutlined />
                </Button>
                <Button
                  className="beam-btn icon-btn mr-2"
                  onClick={() => {
                    if (playSpeed > 0) {
                      setPlaySpeed(playSpeed - 1);
                    }
                  }}
                >
                  <MinusOutlined />
                </Button>
                <Button
                  className="beam-btn icon-btn mr-2"
                  onClick={() => {
                    if (playSpeed < TIMELINE_SPEEDS.length - 1) {
                      setPlaySpeed(playSpeed + 1);
                    }
                  }}
                >
                  <PlusOutlined />
                </Button>
              </React.Fragment>
            )}
            <Button
              className="beam-btn icon-btn mr-2"
              onClick={() => {
                const scrollWidthBefore = timelineRef.current.scrollWidth;
                setZoom(zoom + DEFAULT_ZOOM);
                setTimeout(() => {
                  const scrollWidthAfter = timelineRef.current.scrollWidth;
                  const percentageIncrease =
                    (scrollWidthAfter - scrollWidthBefore) / scrollWidthBefore +
                    1;
                  timelineRef.current.scrollLeft =
                    timelineRef.current.scrollLeft * percentageIncrease +
                    DEFAULT_ZOOM;
                }, 100);
              }}
            >
              <ZoomInOutlined />
            </Button>
            <Button
              className="beam-btn icon-btn"
              onClick={() => {
                if (zoom > DEFAULT_ZOOM) {
                  setZoom(zoom - DEFAULT_ZOOM);
                }
              }}
            >
              <ZoomOutOutlined />
            </Button>
          </div>
          <Row
            className="timeline mt-2"
            ref={timelineRef}
            style={{ overflow: "scroll" }}
            onScroll={(el: any) => {
              if (
                el.target.scrollLeft ===
                el.target.scrollWidth - el.target.clientWidth
              ) {
                if (timeDiff.length > timeDiff.limit) {
                  setTimeDiff({
                    offset: timeDiff.offset + DEFAULT_LIMIT,
                    limit: timeDiff.limit + DEFAULT_LIMIT,
                    length: timeDiff.length,
                  });
                  el.target.scrollLeft = 5;
                } else {
                  setPlay(false);
                }
              } else if (el.target.scrollLeft === 0 && timeDiff.offset !== 0) {
                setTimeDiff({
                  offset: timeDiff.offset - DEFAULT_LIMIT,
                  limit: timeDiff.limit - DEFAULT_LIMIT,
                  length: timeDiff.length,
                });
                el.target.scrollLeft =
                  el.target.scrollWidth - el.target.clientWidth - 5;
              }
            }}
          >
            <div style={{ position: "relative" }}>
              <div className="main-bar">
                {_.range(timeDiff.offset, timeDiff.limit).map((i) => {
                  let timePoint = null;
                  let breakPoint = null;
                  let points = checkin(i - 1);
                  if ((i - 1) % 5 === 0)
                    timePoint = <h6>{getTime(i - 1, true)}</h6>;
                  if (points && points.length > 0)
                    breakPoint = (
                      <React.Fragment>
                        {points.map((point, i) => (
                          <div
                            className="break-point"
                            style={{ paddingLeft: i * 10 }}
                          >
                            <Button
                              className="beam-btn icon-btn"
                              onClick={() => {
                                const query = new URLSearchParams(search);
                                const connectionId = query.get("connectionId");
                                socket.publishToEvent(
                                  SocketTopics.PublishPageTopics,
                                  getFocusMessagePublishTopic(
                                    page?.objectId,
                                    connectionId
                                  ),
                                  {
                                    messageId: point.id,
                                  }
                                );
                              }}
                            >
                              <img src={`/images/${point.name}.png`} alt="" />
                            </Button>
                            {points.length === 1 && (
                              <h5 className="mt-2">{point.type}</h5>
                            )}
                            {i == Math.floor(points.length / 2) && (
                              <p className="mt-1">{getTime(i - 1)}</p>
                            )}
                          </div>
                        ))}
                      </React.Fragment>
                    );
                  if (timePoint != null || breakPoint != null)
                    return (
                      <div
                        className={`time-point mark-time ${
                          breakPoint
                            ? points?.[points.length - 1]?.icon + "-icon break"
                            : ""
                        }`}
                        style={{ width: zoom }}
                        key={i}
                      >
                        {timePoint}
                        {breakPoint}
                      </div>
                    );
                  else
                    return (
                      <div
                        className="time-point"
                        style={{ width: zoom }}
                        key={i}
                      ></div>
                    );
                })}
              </div>
            </div>
          </Row>

          <Row className="custom-table mt-4">
            <Col span={24}>
              <Row className="header">
                <Col span={5}>Event</Col>
                <Col span={4}>Flag</Col>
                <Col span={4}>User</Col>
                <Col span={4}>Time</Col>
                <Col span={4}>Status</Col>
                <Col span={3}>Location</Col>
              </Row>

              {messages &&
                messages.map((event, i) => (
                  <Row className="table-row d-flex">
                    <Col span={5} className="event-column">
                      <div
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          const query = new URLSearchParams(search);
                          const connectionId = query.get("connectionId");
                          socket.publishToEvent(
                            SocketTopics.PublishPageTopics,
                            getFocusMessagePublishTopic(
                              page?.objectId,
                              connectionId
                            ),
                            {
                              messageId: event.id,
                            }
                          );
                        }}
                      >
                        <img
                          src={`/images/${getEventIcon(event)?.icon ?? 'text-icon'}.png`}
                          alt=""
                        />
                      </div>
                      <span>{event.messageType.toUpperCase()}</span>
                    </Col>

                    <Col span={4}>
                      {event?.status?.onMoveEvent && (
                        <ReactSVG
                          src="/images/flag-icon.svg"
                          className={`${event?.status?.color ?? "gray"}-icon`}
                        />
                      )}
                    </Col>
                    <Col span={4}>
                      {users.find((x) => {
                        return x.id === event.senderId;
                      })?.fullName ?? `user # ${event.senderId}`}
                    </Col>
                    <Col span={4}>
                      {getLocalDatetimeString(event.createdAt)}
                    </Col>
                    <Col span={4}>
                      {event.status && (
                        <label
                          className={`${event?.status?.color || "gray"}-icon`}
                        >
                          {event.status.name}
                        </label>
                      )}
                    </Col>

                    <Col span={3}>
                      {(event?.data?.coordinates ||
                        event?.data?.location?.coordinates) && (
                        <button
                          type="button"
                          className="ant-btn beam-btn icon-btn"
                          onClick={() => {
                            window.open(
                              "https://maps.google.com/?q=" +
                                (event?.data?.coordinates?.lat ||
                                  event?.data?.location?.coordinates?.lat) +
                                "," +
                                (event?.data?.coordinates?.long ||
                                  event?.data?.location?.coordinates?.long)
                            );
                          }}
                        >
                          <img src={`/images/location.png`} alt="" />
                        </button>
                      )}
                    </Col>
                  </Row>
                ))}
            </Col>
          </Row>
        </React.Fragment>
      )}
    </Modal>
  );
};

export default AnalyticsSummaryModal;
